import React, { useState } from "react";
import DateRangeFilter from "../../../components/shared/fields/DateRangeFilter";
import useTranslator from "../../../hooks/useTranslator";
import { getCurrentDate, makePriorLotNumber } from "../../../helpers/Utils";
import DashboardSection from "../../dashboard/components/DashboardSection";
import "../../dashboard/components/dashboard-sections.css";
import { PackageIconSvg } from "../../../svgFiles/PackageIconSvg";
import { ScaleIconSvg } from "../../../svgFiles/ScaleIconSvg";
import { FlameIconSvg } from "../../../svgFiles/FlameIconSvg";
import { WashIconSvg } from "../../../svgFiles/WashIconSvg";
import { RipeningIconSvg } from "../../../svgFiles/RipeningIconSvg";
import { CycleIconSvg } from "../../../svgFiles/CycleIconSvg";
import { BarChartIconSvg } from "../../../svgFiles/BarChartIconSvg";
import {
  useGetReworkAreaSummaryQuery,
  useGetReworkPackingSummaryQuery,
  useGetToastedSummaryQuery,
} from "../rework/reworkApi";
import { useGetHumidSummaryQuery } from "../humid/humidApi";
import { useGetGreenRoomSummaryQuery } from "../greenroom/greenRoomApi";

const MetricBox = ({ label, value, unit }) => (
  <div className="db-evap-box">
    <div className="db-evap-label">{label}</div>
    <div className="db-evap-value">
      {value ?? "0"}
      {unit && <span className="db-metric-unit"> {unit}</span>}
    </div>
  </div>
);

const ReworkDashboard = () => {
  const { _t } = useTranslator();
  const currentDate = getCurrentDate("YYYY-MM-DD");
  const [date, setDate] = useState(currentDate);
  const [isSingleDay, setIsSingleDay] = useState(true);
  const [, setStartDate] = useState(currentDate);
  const [, setEndDate] = useState(currentDate);
  const [lotNumber, setLotNumber] = useState(makePriorLotNumber(currentDate));

  const handleApplyRange = ({ startDate: start, endDate: end }) => {
    const single = start === end;
    setIsSingleDay(single);
    setStartDate(start);
    setEndDate(end);
    setDate(start);
    if (single) setLotNumber(makePriorLotNumber(start));
  };

  const { data: reworkSummaryData } = useGetReworkAreaSummaryQuery({
    tab: "rework",
    date,
    lot_number: lotNumber,
  });
  const { data: reworkPackingSummaryData } = useGetReworkPackingSummaryQuery({
    date,
    lot_number: lotNumber,
  });
  const { data: toastedSummaryData } = useGetToastedSummaryQuery(
    {},
    { refetchOnMountOrArgChange: true },
  );
  const { data: humidSummaryData } = useGetHumidSummaryQuery({ date });
  const { data: greenRoomSummaryData } = useGetGreenRoomSummaryQuery({ date });

  // Rework Area Items
  const reworkItems = [
    {
      label: _t("total_prime_kg", "Total Prime KG"),
      value: reworkSummaryData?.data?.total_no_of_prime_kg || 0,
      unit: " KG",
    },
    {
      label: _t("prime_cheeks_kg", "Prime Cheeks KG"),
      value: reworkSummaryData?.data?.total_no_of_prime_kg_in_cheeks || 0,
      unit: " KG",
    },
    {
      label: _t("prime_strips_kg", "Prime Strips KG"),
      value: reworkSummaryData?.data?.total_no_of_prime_kg_in_strips || 0,
      unit: " KG",
    },
    {
      label: _t("rejected_kg", "Rejected KG"),
      value: reworkSummaryData?.data?.total_no_of_trash_kg || 0,
      unit: " KG",
    },
    {
      label: _t("seconds_cheeks_kg", "Seconds Cheeks KG"),
      value: reworkSummaryData?.data?.seconds_cheeks_kg || 0,
      unit: " KG",
    },
    {
      label: _t("seconds_strips_kg", "Seconds Strips KG"),
      value: reworkSummaryData?.data?.seconds_strips_kg || 0,
      unit: " KG",
    },
    {
      label: _t("total_tapita_kg", "Total Tapita KG"),
      value: reworkSummaryData?.data?.total_tapita_kg || 0,
      unit: " KG",
    },
    {
      label: _t("total_pieces_kg", "Total Pieces KG"),
      value: reworkSummaryData?.data?.total_pieces_kg || 0,
      unit: " KG",
    },
  ];

  // Rework Packing Items
  const reworkPackingItems = [
    {
      label: _t("total_boxes", "Total Boxes"),
      value: reworkPackingSummaryData?.data?.total_boxes_packed || 0,
    },
    {
      label: _t("prime_cheek_boxes_10_pound", "Prime Cheek Boxes (10 pound)"),
      value:
        reworkPackingSummaryData?.data?.total_prime_cheek_boxes_10_pound || 0,
    },
    {
      label: _t("prime_cheek_boxes_20_pound", "Prime Cheek Boxes (20 pound)"),
      value:
        reworkPackingSummaryData?.data?.total_prime_cheek_boxes_20_pound || 0,
    },
    {
      label: _t("prime_strip_boxes_20_pound", "Prime Strip Boxes (20 pound)"),
      value:
        reworkPackingSummaryData?.data?.total_prime_strip_boxes_20_pound || 0,
    },
    {
      label: _t("seconds_cheek_boxes_20_pound", "Seconds Cheek Boxes (20 pound)"),
      value:
        reworkPackingSummaryData?.data?.total_seconds_cheek_boxes_20_pound || 0,
    },
    {
      label: _t("seconds_strip_boxes_20_pound", "Seconds Strip Boxes (20 pound)"),
      value:
        reworkPackingSummaryData?.data?.total_seconds_strip_boxes_20_pound || 0,
    },
    {
      label: _t("tapitas_boxes_20_pound", "Tapitas Boxes (20 pound)"),
      value:
        reworkPackingSummaryData?.data?.total_tapitas_boxes_20_pound || 0,
    },
    {
      label: _t("pieces_boxes_20_pound", "Pieces Boxes (20 pound)"),
      value:
        reworkPackingSummaryData?.data?.total_pieces_boxes_20_pound || 0,
    },
  ];

  // Toasted Items
  const toastedItems = [
    {
      label: _t("total_kg_toasted", "Total KG Toasted"),
      value: (toastedSummaryData?.data?.total_kg_cheeks_in_toasted || 0) + (toastedSummaryData?.data?.total_kg_strips_in_toasted || 0),
      unit: " KG",
    },
    {
      label: _t("total_carts_in_hydrating", "Total Carts in Hydrating"),
      value: toastedSummaryData?.data?.total_carts_in_hydrating || 0,
    },
    {
      label: _t("total_kg_bagged", "Total kg bagged"),
      value: (toastedSummaryData?.data?.total_cheeks_kg_bagged || 0) + (toastedSummaryData?.data?.total_strips_kg_bagged || 0),
      unit: " KG",
    },
  ];

  // Humid Items
  const humidItems = [
    {
      label: _t("total_kg_humid", "Total KG Humid"),
      value: (humidSummaryData?.data?.total_kg_cheeks_in_humidity || 0) + (humidSummaryData?.data?.total_kg_strips_in_humidity || 0),
      unit: " KG",
    },
    {
      label: _t("total_carts_in_oven", "Total Carts in Oven"),
      value: humidSummaryData?.data?.total_carts_in_ovens || 0,
    },
    {
      label: _t("carts_in_cooling_room", "Carts in Cooling Room"),
      value: humidSummaryData?.data?.total_carts_in_cooling_room || 0,
    },
    {
      label: _t("total_kg_bagged", "Total KG Bagged"),
      value: (humidSummaryData?.data?.total_cheeks_kg_bagged || 0) + (humidSummaryData?.data?.total_strips_kg_bagged || 0),
      unit: " KG",
    },
    {
      label: _t("evaporation_prcnt", "Evaporation %"),
      value: humidSummaryData?.data?.evaporation_prcnt || 0,
    },
  ];

  // Sorting Items
  const sortingItems = [
    {
      label: _t("total_kg_prime", "Total Kg Prime"),
      value: reworkSummaryData?.data?.total_no_of_prime_kg || 0,
      unit: " KG",
    },
    {
      label: _t("total_kg_trash", "Total Kg Trash"),
      value: reworkSummaryData?.data?.total_no_of_trash_kg || 0,
      unit: " KG",
    },
    {
      label: _t("total_kg_seconds", "Total KG Seconds"),
      value: (reworkSummaryData?.data?.seconds_cheeks_kg || 0) + (reworkSummaryData?.data?.seconds_strips_kg || 0),
      unit: " KG",
    },
  ];

  // Packing Items (for Sorting and Packing section)
  const packingSortingItems = [
    {
      label: _t("total_boxes_packed", "Total Boxes Packed"),
      value: reworkPackingSummaryData?.data?.total_boxes_packed || 0,
    },
    {
      label: _t("total_prime_cheek_boxes", "Total Prime Cheek Boxes"),
      value: (reworkPackingSummaryData?.data?.total_prime_cheek_boxes_10_pound || 0) + (reworkPackingSummaryData?.data?.total_prime_cheek_boxes_20_pound || 0),
    },
    {
      label: _t("total_other_cheek_boxes", "Total Other Cheek Boxes"),
      value: reworkPackingSummaryData?.data?.total_seconds_cheek_boxes_20_pound || 0,
    },
    {
      label: _t("total_prime_strips_boxes", "Total Prime Strips Boxes"),
      value: reworkPackingSummaryData?.data?.total_prime_strip_boxes_20_pound || 0,
    },
    {
      label: _t("total_other_strips_boxes", "Total Other Strips Boxes"),
      value: reworkPackingSummaryData?.data?.total_seconds_strip_boxes_20_pound || 0,
    },
  ];

  // Metrics Items
  const metricsItems = [
    {
      label: _t("prime_cheek_prcnt", "Prime Cheek %"),
      value: 0,
    },
    {
      label: _t("prime_strips_prcnt", "Prime Strips %"),
      value: 0,
    },
    {
      label: _t("total_prime_prcnt", "Total Prime %"),
      value: 0,
    },
    {
      label: _t("total_trash_prcnt", "Total Trash %"),
      value: 0,
    },
    {
      label: _t("total_seconds_prcnt", "Total Seconds %"),
      value: 0,
    },
  ];

  // Green Room Items
  const greenRoomItems = [
    {
      label: _t("total_green_room_cheeks_kg", "Total Green Room Cheeks Kg"),
      value: greenRoomSummaryData?.data?.total_kg_cheeks_in_green_room || 0,
      unit: " KG",
    },
    {
      label: _t("total_green_room_strips_kg", "Total Green Room Strips Kg"),
      value: greenRoomSummaryData?.data?.total_kg_strips_in_green_room || 0,
      unit: " KG",
    },
    {
      label: _t("total_kg_green_room", "Total KG in Green Room"),
      value: (greenRoomSummaryData?.data?.total_kg_cheeks_in_green_room || 0) + (greenRoomSummaryData?.data?.total_kg_strips_in_green_room || 0),
      unit: " KG",
    },
  ];

  return (
    <div>
      {/* ── Toolbar with Date Filter ──────────────────────────── */}
      <div className="packing_dashboard_toolbar mb-4 align-items-center">
        <h5 className="packing_dashboard_toolbar_title m-0">
          {_t("rework_overview_title", "Rework Overview")}
        </h5>
        <div className="packing_toolbar_right">
          {isSingleDay && (
            <div className="packing_filter_group">
              <label className="packing_filter_label">
                {_t("lot_number", "Lot Number")}
              </label>
              <div className="packing_lot_number_row">
                <div className="packing_lot_chip borderRedBtn cmn_btn btn-small">
                  #{lotNumber}
                </div>
              </div>
            </div>
          )}
          <div className="packing_filter_group">
            <label htmlFor="rework-date-range-filter" className="packing_filter_label">
              {_t("date_range", "Date Range")}
            </label>
            <DateRangeFilter
              setDate={setDate}
              onApplyRange={handleApplyRange}
              triggerId="rework-date-range-filter"
            />
          </div>
        </div>
      </div>

      <div>
        {/* ── Rework Area Section (2 rows in accordion) ── */}
        <DashboardSection
          title={
            <span className="d-flex align-items-center gap-2">
              <ScaleIconSvg size={18} color="#4ade80" />
              {_t("rework_area_overview", "Rework Area Overview")}
            </span>
          }
        >
          {/* First Row */}
          <div style={{ display: "grid", gridTemplateColumns: "repeat(4, 1fr)", gap: "12px", marginBottom: "16px" }}>
            {reworkItems.slice(0, 4).map((item, i) => (
              <div
                key={i}
                style={{
                  backgroundColor: "#f9fafb",
                  border: "1px solid #f0f1f3",
                  borderRadius: "8px",
                  padding: "16px",
                  display: "flex",
                  flexDirection: "column",
                  gap: "12px",
                }}
              >
                <div style={{ fontSize: "11px", fontWeight: "600", color: "#667085", textTransform: "uppercase", letterSpacing: "0.5px" }}>
                  {item.label}
                </div>
                <div style={{ fontSize: "20px", fontWeight: "700", color: "#4ade80" }}>
                  {item.value}{item.unit}
                </div>
              </div>
            ))}
          </div>

          {/* Second Row */}
          <div style={{ display: "grid", gridTemplateColumns: "repeat(4, 1fr)", gap: "12px" }}>
            {reworkItems.slice(4).map((item, i) => (
              <div
                key={i}
                style={{
                  backgroundColor: "#f9fafb",
                  border: "1px solid #f0f1f3",
                  borderRadius: "8px",
                  padding: "16px",
                  display: "flex",
                  flexDirection: "column",
                  gap: "12px",
                }}
              >
                <div style={{ fontSize: "11px", fontWeight: "600", color: "#667085", textTransform: "uppercase", letterSpacing: "0.5px" }}>
                  {item.label}
                </div>
                <div style={{ fontSize: "20px", fontWeight: "700", color: "#4ade80" }}>
                  {item.value}{item.unit}
                </div>
              </div>
            ))}
          </div>
        </DashboardSection>        
          {/* ── Rework Packing Section ── */}
          <DashboardSection
            title={
              <span className="d-flex align-items-center gap-2">
                <PackageIconSvg size={18} color="#fb923c" />
                {_t("rework_packing_overview", "Rework Packing Overview")}
              </span>
            }
          >
            <div className="pp-brokering-grid p-0">
              {reworkPackingItems.map((item, i) => (
                <div key={i} className="pp-brokering-box">
                  <div className="pp-brokering-box-label">
                    {_t(item.label, item.label)}
                  </div>
                  <div className="pp-brokering-box-value">
                    {item.value ?? "0"}
                    {item.unit && (
                      <span className="db-metric-unit"> {item.unit}</span>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </DashboardSection>
{/* ── Sorting and Packing Section ── */}
        <DashboardSection
          title={
            <span className="d-flex align-items-center gap-2">
              <CycleIconSvg size={18} color="#60a5fa" />
              {_t("sorting_and_packing", "Sorting and Packing")}
            </span>
          }
        >
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "16px" }}>
            {/* Sorting Subsection */}
            <div className="db-avg-card">
              <div className="db-avg-header">
                <span className="db-avg-header-title d-flex align-items-center gap-2">
                  <ScaleIconSvg size={15} color="#4ade80" />
                  {_t("sorting", "Sorting")}
                </span>
              </div>
              <div className="db-evap-row" style={{ padding: "16px 18px" }}>
                {sortingItems.map((item, i) => (
                  <MetricBox
                    key={i}
                    label={item.label}
                    value={item.value}
                    unit={item.unit}
                  />
                ))}
              </div>
            </div>

            {/* Packing Subsection */}
            <div className="db-avg-card">
              <div className="db-avg-header">
                <span className="db-avg-header-title d-flex align-items-center gap-2">
                  <PackageIconSvg size={15} color="#fb923c" />
                  {_t("packing", "Packing")}
                </span>
              </div>
              <div className="db-evap-row" style={{ padding: "16px 18px" }}>
                {packingSortingItems.map((item, i) => (
                  <MetricBox
                    key={i}
                    label={item.label}
                    value={item.value}
                    unit={item.unit}
                  />
                ))}
              </div>
            </div>
          </div>
        </DashboardSection>
          {/* ── Humidity Room Section ── */}
          <DashboardSection
            title={
              <span className="d-flex align-items-center gap-2">
                <WashIconSvg size={18} color="#60a5fa" />
                {_t("humidity_room_overview", "Humidity Room Overview")}
              </span>
            }
          >
            <div className="pp-brokering-grid p-0">
              {humidItems.map((item, i) => (
                <div key={i} className="pp-brokering-box">
                  <div className="pp-brokering-box-label">
                    {_t(item.label, item.label)}
                  </div>
                  <div className="pp-brokering-box-value">
                    {item.value ?? "0"}
                    {item.unit && (
                      <span className="db-metric-unit"> {item.unit}</span>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </DashboardSection>

          {/* ── Toasted Room Section ── */}
          <DashboardSection
            title={
              <span className="d-flex align-items-center gap-2">
                <FlameIconSvg size={18} color="#fb923c" />
                {_t("toasted_room_overview", "Toasted Room Overview")}
              </span>
            }
          >
            <div className="pko-grid-5 p-0">
              {toastedItems.map((item, i) => (
                <MetricBox
                  key={i}
                  label={item.label}
                  value={item.value}
                  unit={item.unit}
                />
              ))}
            </div>
          </DashboardSection>

          {/* ── Green Room Section ── */}
          <DashboardSection
            title={
              <span className="d-flex align-items-center gap-2">
                <RipeningIconSvg size={18} color="#4ade80" />
                {_t("green_room_overview", "Green Room Overview")}
              </span>
            }
          >
            <div className="db-avg-card p-0">
              {greenRoomItems.map((item, i) => (
                <div key={i} className="db-avg-row">
                  <span>{_t(item.label, item.label)}</span>
                  <span className="db-avg-val">
                    {item.value ?? "0"}
                    {item.unit && (
                      <span className="db-metric-unit"> {item.unit}</span>
                    )}
                  </span>
                </div>
              ))}
            </div>
          </DashboardSection>

        {/* ── Metrics Section ── */}
        <DashboardSection
          title={
            <span className="d-flex align-items-center gap-2">
              <BarChartIconSvg size={18} color="#60a5fa" />
              {_t("metrics", "Metrics")}
            </span>
          }
        >
          <div className="db-inner-metric-row p-0">
            {metricsItems.map((item, i) => (
              <div key={i} className="db-inner-metric">
                <div className="db-metric-label">
                  {_t(item.label, item.label)}
                </div>
                <div className="db-metric-value">
                  {item.value ?? "0"}%
                </div>
              </div>
            ))}
          </div>
        </DashboardSection>
      </div>
    </div>
  );
};

export default ReworkDashboard;
